import { CalendarMonth, Person, WorkspacePremium } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Grid2 as Grid, Typography } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { useMySplitTokensContext } from '../../../hooks/useMySplitTokensContext';
import { useUserContext } from '../../../hooks/useUserContext';
import { SplitCoinImage } from '../../common/SplitCoins';
import { SplitItProChip } from './SplitItProChip';

interface UserHeaderProps {
  showButtons?: boolean;
}

export const UserHeader: React.FC<UserHeaderProps> = ({ showButtons = true }) => {
  const { user } = useUserContext();
  const { mySplitTokens } = useMySplitTokensContext();

  const balance = mySplitTokens.availableTokens;

  if (!user) return null;

  const isPro = user.subscription?.isActive();

  return (
    <Card sx={{ mb: 1 }}>
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {user.profileImageUrl ? (
                <Avatar sx={{ width: 56, height: 56, mr: 2 }} src={user.profileImageUrl} alt={user.name} />
              ) : (
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', mr: 2 }}>
                  <Person />
                </Avatar>
              )}
              <Box>
                <Typography variant="h5">{user.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <CalendarMonth sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                    Member since {user.memberSince}
                  </Typography>
                  {user?.subscription?.isActive() && <SplitItProChip type={user.subscription.type} />}
                </Box>
              </Box>
            </Box>
          </Grid>
          {showButtons && (
            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon="🧾"
                size="large"
                sx={{
                  mb: { xs: 1, sm: 0 },
                  mr: { sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                  py: { xs: 1.5, sm: 1 },
                  fontSize: { xs: '1.1rem', sm: '0.875rem' },
                  fontWeight: { xs: 'bold', sm: 'bold' },
                }}
                component={Link}
                to="/new"
              >
                New Split
              </Button>
              {!isPro && (
                <Button
                  startIcon={<WorkspacePremium />}
                  variant="contained"
                  color="success"
                  component={Link}
                  to="/tokens/buy"
                  sx={{
                    mb: { xs: 1, sm: 0 },
                    mr: { sm: 1 },
                    width: { xs: '100%', sm: 'auto' },
                    border: '1px solid',
                  }}
                >
                  Upgrade to Split It Pro
                </Button>
              )}
              <Button
                startIcon={<SplitCoinImage large={false} />}
                variant="outlined"
                component={Link}
                to="/tokens"
                sx={{ mr: { sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
              >
                {isPro ? 'Manage Subscription' : `${balance} SplitCoin${balance === 1 ? '' : 's'}`}
              </Button>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
